/**
 * LiveKitTestPage — SFU akışını test etmek için minimal UI.
 *
 * URL: /livekit-test/:roomId?
 *   - Authenticated user gerekli (ProtectedRoute içinde)
 *   - 2 sekmede aynı roomId ile aç → 2 katılımcılı toplantı testi
 *   - Mevcut MeMeet (P2P) kodu hiç dokunulmaz
 *
 * Backend: POST https://prod.apiollon.com/flow/memeet-livekit-token
 *   Bearer = AuthContext.token
 *   Body: { roomID, name }
 */
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
// @ts-ignore — JS module
import { useAuth } from "../context/AuthContext";
import { useLiveKitRoom, type LiveKitParticipant } from "../hooks/useLiveKitRoom";
import { ConnectionState } from "livekit-client";
import "./LiveKitTestPage.css";

const API_URL = (process.env.REACT_APP_API_URL as string) || "https://prod.apiollon.com/";
const TOKEN_ENDPOINT = `${API_URL.replace(/\/+$/, "/")}flow/memeet-livekit-token`;

function ParticipantTile({ p }: { p: LiveKitParticipant }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (videoRef.current && p.videoTrack) {
      const stream = new MediaStream([p.videoTrack]);
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch(() => {});
    } else if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, [p.videoTrack]);

  useEffect(() => {
    if (audioRef.current && p.audioTrack && !p.isLocal) {
      const stream = new MediaStream([p.audioTrack]);
      audioRef.current.srcObject = stream;
      audioRef.current.play().catch(() => {});
    } else if (audioRef.current) {
      audioRef.current.srcObject = null;
    }
  }, [p.audioTrack, p.isLocal]);

  return (
    <div className={`lk-tile ${p.speaking ? "lk-speaking" : ""}`}>
      <video ref={videoRef} autoPlay playsInline muted={p.isLocal} className="lk-video" />
      {!p.isLocal && <audio ref={audioRef} autoPlay />}
      <div className="lk-tile-info">
        <span className="lk-name">
          {p.name || p.identity}
          {p.isLocal ? " (Sen)" : ""}
        </span>
        <span className="lk-meta">
          {p.audioMuted ? "🔇" : "🎤"} {p.videoMuted ? "📷❌" : "📷"} ·{" "}
          {p.connectionQuality}
        </span>
      </div>
    </div>
  );
}

/** Screen share tile — büyük, ayrı render */
function ScreenShareTile({ p }: { p: LiveKitParticipant }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (videoRef.current && p.screenShareTrack) {
      const stream = new MediaStream([p.screenShareTrack]);
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch(() => {});
    }
  }, [p.screenShareTrack]);

  useEffect(() => {
    if (audioRef.current && p.screenShareAudioTrack && !p.isLocal) {
      const stream = new MediaStream([p.screenShareAudioTrack]);
      audioRef.current.srcObject = stream;
      audioRef.current.play().catch(() => {});
    }
  }, [p.screenShareAudioTrack, p.isLocal]);

  return (
    <div className="lk-screen-tile">
      <video ref={videoRef} autoPlay playsInline muted={p.isLocal} className="lk-screen-video" />
      {!p.isLocal && p.screenShareAudioTrack && <audio ref={audioRef} autoPlay />}
      <div className="lk-screen-label">
        🖥️ {p.name || p.identity}{p.isLocal ? " (sen)" : ""} ekranını paylaşıyor
      </div>
    </div>
  );
}

function LiveKitTestPage() {
  const { roomId: urlRoomId } = useParams<{ roomId?: string }>();
  const { token, user } = useAuth();
  const [roomId, setRoomId] = useState(urlRoomId || "test-livekit-001");
  const [error, setError] = useState<string | null>(null);

  const lk = useLiveKitRoom({
    tokenEndpoint: TOKEN_ENDPOINT,
    authToken: token || undefined,
    onError: (e) => setError(e.message),
  });

  const handleConnect = async () => {
    if (!roomId.trim()) return;
    setError(null);
    try {
      await lk.connect(roomId.trim(), {
        displayName: user?.name || user?.email || "Anonymous",
        audio: true,
        video: true,
      });
    } catch (e: any) {
      setError(e?.message || "connect_failed");
    }
  };

  const handleDisconnect = async () => {
    await lk.disconnect();
  };

  const stateLabel = (s: ConnectionState) => {
    switch (s) {
      case ConnectionState.Connecting:    return "Bağlanıyor…";
      case ConnectionState.Connected:     return "Bağlı";
      case ConnectionState.Reconnecting:  return "Yeniden bağlanıyor…";
      case ConnectionState.Disconnected:  return "Bağlı değil";
      default:                            return s;
    }
  };

  const isConnected = lk.connectionState === ConnectionState.Connected;
  const allParticipants: LiveKitParticipant[] = [
    ...(lk.localParticipant ? [lk.localParticipant] : []),
    ...lk.participants,
  ];
  // Ekran paylaşan kişi varsa onu büyük tile olarak göster
  const sharingParticipant = allParticipants.find((p) => p.screenShareTrack);

  const handleScreenShareToggle = async () => {
    setError(null);
    try {
      if (lk.isScreenSharing) {
        await lk.stopScreenShare();
      } else {
        await lk.startScreenShare();
      }
    } catch (e: any) {
      // Kullanıcı iptal etti veya tarayıcı reddetti
      const msg = e?.message || "screen_share_failed";
      if (/permission|denied|cancel|not allowed/i.test(msg)) {
        setError("Ekran paylaşımı iptal edildi veya izin verilmedi");
      } else {
        setError(`Ekran paylaşımı başlatılamadı: ${msg}`);
      }
    }
  };

  return (
    <div className="lk-test-page">
      <div className="lk-test-header">
        <h1>🚀 LiveKit SFU Test</h1>
        <p className="lk-state">Durum: <strong>{stateLabel(lk.connectionState)}</strong></p>
      </div>

      <div className="lk-test-controls">
        <input
          className="lk-input"
          placeholder="Room ID (örn: test-livekit-001)"
          value={roomId}
          onChange={(e) => setRoomId(e.target.value)}
          disabled={isConnected || lk.connectionState === ConnectionState.Connecting}
        />
        {!isConnected ? (
          <button
            className="lk-btn lk-btn-primary"
            onClick={handleConnect}
            disabled={!roomId.trim() || lk.connectionState === ConnectionState.Connecting}
          >
            {lk.connectionState === ConnectionState.Connecting ? "Bağlanıyor…" : "Odaya Katıl"}
          </button>
        ) : (
          <>
            <button className="lk-btn" onClick={lk.toggleMic}>
              {lk.isMicEnabled ? "🎤 Mic Açık" : "🔇 Mic Kapalı"}
            </button>
            <button className="lk-btn" onClick={lk.toggleCamera}>
              {lk.isCameraEnabled ? "📷 Kamera Açık" : "📷❌ Kamera Kapalı"}
            </button>
            <button className="lk-btn" onClick={handleScreenShareToggle}>
              {lk.isScreenSharing ? "🖥️ Paylaşımı Bitir" : "🖥️ Ekran Paylaş"}
            </button>
            <button className="lk-btn lk-btn-danger" onClick={handleDisconnect}>
              ⛔ Ayrıl
            </button>
          </>
        )}
      </div>

      {error && (
        <div className="lk-error">
          ❌ {error}
        </div>
      )}

      <div className="lk-test-info">
        <span>📍 Room: <code>{roomId}</code></span>
        <span>👥 Katılımcı: <strong>{allParticipants.length}</strong></span>
        <span>🌐 Endpoint: <code>{TOKEN_ENDPOINT}</code></span>
      </div>

      {sharingParticipant && (
        <ScreenShareTile p={sharingParticipant} />
      )}

      <div className={sharingParticipant ? "lk-grid lk-grid-compact" : "lk-grid"}>
        {allParticipants.length === 0 ? (
          <div className="lk-empty">
            <p>Henüz odaya bağlı değilsin.</p>
            <p>Test için: 2 farklı tarayıcı sekmesinde aynı Room ID ile katıl.</p>
          </div>
        ) : (
          allParticipants.map((p) => <ParticipantTile key={p.identity} p={p} />)
        )}
      </div>
    </div>
  );
}

export default LiveKitTestPage;
